(ns frontend.view.modals.ai-deal-one-pager.model
  "Pure state helpers for the AI deal one-pager generation modal."
  (:require
   [clojure.string :as string]
   [common.schema.entities.integration.ai-options :as ai-options]
   [frontend.ui.ai-summary-configuration.model :as configuration-model]))

(def root-path [::state])

(def active-statuses
  #{ai-options/STATUS-PENDING
    ai-options/STATUS-PROCESSING})

(def failed-statuses
  #{ai-options/STATUS-FAILED
    ai-options/STATUS-TOKEN-EXHAUSTED})

(defn initial-state
  [deal-id pipeline-id]
  {:deal-id deal-id
   :pipeline-id pipeline-id
   :request-state :loading
   :library-state :loading
   :configurations []
   :selected-configuration-id nil
   :configuration-detail-state :idle
   :configuration-detail nil
   :draft nil
   :saved-draft nil
   :copy-source-name nil
   :copy-previous-name nil
   :create-mode? false
   :create-previous-id nil
   :save-state :idle
   :uploading-field nil
   :generation nil
   :deduplicated? false
   :error-category nil})

(defn pipeline-default-id
  [configurations pipeline-id]
  (some (fn [configuration]
          (when (some #{pipeline-id} (:default-pipeline-ids configuration))
            (:id configuration)))
        configurations))

(defn selected-configuration
  [{:keys [configurations selected-configuration-id]}]
  (some (fn [configuration]
          (when (= selected-configuration-id (:id configuration))
            configuration))
        configurations))

(declare draft-valid?)

(defn generation-ready?
  [state]
  (and (= :ready (:library-state state))
       (= :ready (:configuration-detail-state state))
       (some? (selected-configuration state))
       (draft-valid? (:draft state))))

(defn configuration-detail->draft
  [configuration]
  (configuration-model/configuration-detail->draft configuration))

(defn draft-dirty?
  [{:keys [draft saved-draft]}]
  (configuration-model/draft-dirty? draft saved-draft))

(defn draft-valid?
  [draft]
  (configuration-model/draft-valid? draft))

(defn copy-mode?
  [state]
  (some? (:copy-source-name state)))

(defn create-mode?
  [state]
  (boolean (:create-mode? state)))

(defn copy-name-valid?
  [{:keys [draft copy-source-name]}]
  (and copy-source-name
       (configuration-model/copy-name-valid? draft copy-source-name)))

(defn run-overrides
  [draft]
  {:summary-instructions (:summary-instructions draft)
   :selected-data-sources (->> (:selected-data-sources draft) sort vec)
   :style-guideline-source (:style-guideline-source draft)
   :style-guideline-document-id (:style-guideline-document-id draft)
   :style-reference-url (:style-reference-url draft)
   :document-template-id (:document-template-id draft)})

(defn configuration-update-data
  [draft]
  (configuration-model/content-data draft))

(defn created-configuration-data
  [draft]
  (configuration-model/create-data draft))

(defn new-configuration-data
  [configuration draft]
  (assoc (configuration-update-data draft)
         :entity/type :entity.type/ai-summary-configuration
         :ai-summary-configuration/applicable-pipelines
         (set (:applicable-pipeline-ids configuration))))

(defn generation-active?
  [generation]
  (contains? active-statuses (:status generation)))

(defn generation-completed?
  [generation]
  (= ai-options/STATUS-COMPLETED (:status generation)))

(defn generation-failed?
  [generation]
  (contains? failed-statuses (:status generation)))

(defn output-attachment
  [generation]
  (when (generation-completed? generation)
    (:output generation)))

(defn error-message
  [error]
  (or (:message error)
      (get-in error [:response :message])
      (get-in error [:response :body :message])
      (get-in error [:response :body :error])))

(defn classify-error
  [error]
  (let [message (some-> (error-message error)
                        str
                        string/lower-case)]
    (cond
      (and message
           (string/includes? message "no ai service is configured"))
      :service-unavailable

      (and message
           (string/includes? message "rate limit reached"))
      :rate-limited

      (contains? #{401 403} (or (:status error)
                                (get-in error [:response :status])))
      :access-denied

      :else
      :request-failed)))
